import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import api from '../api/client';
import CategoryIcon from './CategoryIcon';
import AddExpenseDialog from './AddExpenseDialog';
import { formatMoney } from './Money';
import { categoryName } from '../i18n';
import { useAuth } from '../context/AuthContext';

const dateLabel = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === 'en' ? 'en-IN' : lang + '-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/** Expenses list grouped by date, with add FAB. access: 'owner' | 'client' | 'worker' */
const ExpensesTab = ({ projectId, expenses, categories, access, onChanged }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);

  const canAdd = access === 'owner' || access === 'client';

  const remove = async (expense) => {
    if (!window.confirm(t('expense.deleteConfirm'))) return;
    await api.delete(`/api/expenses/${expense.id}`);
    onChanged();
  };

  /* group by date, newest first (list already sorted by the API) */
  const groups = [];
  for (const e of expenses) {
    const key = e.expense_date.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(e);
    else groups.push({ key, items: [e] });
  }

  return (
    <Box>
      {expenses.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="subtitle1">{t('expense.empty')}</Typography>
            {canAdd && (
              <Typography variant="body2" color="text.secondary">
                {t('expense.emptyHint')}
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {groups.map((group) => (
            <Box key={group.key}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, ml: 0.5 }}>
                {dateLabel(group.key, i18n.language)}
              </Typography>
              <Card sx={{ mt: 0.5 }}>
                {group.items.map((e, idx) => {
                  const canDelete = access === 'owner' || e.created_by === user?.id;
                  return (
                    <Stack
                      key={e.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderTop: idx > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                      })}
                    >
                      <Avatar sx={{ bgcolor: 'action.hover', width: 40, height: 40 }}>
                        <CategoryIcon icon={e.category_icon} sx={{ fontSize: 20, color: 'text.secondary' }} />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {e.paid_to || categoryName({
                            name_en: e.category_name_en,
                            name_hi: e.category_name_hi,
                            name_te: e.category_name_te,
                          })}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {categoryName({
                              name_en: e.category_name_en,
                              name_hi: e.category_name_hi,
                              name_te: e.category_name_te,
                            })}
                            {e.note ? ` · ${e.note}` : ''}
                          </Typography>
                        </Stack>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {formatMoney(e.amount)}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {e.paid_by === 'client' && (
                            <Chip
                              size="small"
                              icon={<PersonIcon sx={{ fontSize: 13 }} />}
                              label={t('project.byClient')}
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                            />
                          )}
                          {access === 'owner' && !e.visible_to_client && (
                            <VisibilityOffIcon sx={{ fontSize: 14, color: 'text.disabled' }} titleAccess={t('expense.hiddenFromClient')} />
                          )}
                        </Stack>
                      </Stack>
                      {canDelete && (
                        <IconButton size="small" onClick={() => remove(e)} aria-label={t('common.delete')}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
              </Card>
            </Box>
          ))}
        </Stack>
      )}

      {canAdd && (
        <>
          <Fab
            color="primary"
            variant="extended"
            onClick={() => setDialogOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
          >
            <AddIcon sx={{ mr: 0.5 }} /> {t('expense.add')}
          </Fab>
          <AddExpenseDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSaved={() => {
              setDialogOpen(false);
              onChanged();
            }}
            projectId={projectId}
            categories={categories}
            access={access}
          />
        </>
      )}
    </Box>
  );
};

export default ExpensesTab;
